// src/pages/settings/ChangePassword.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  LinearProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Chip,
   
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { baseUrl } from "../../constants";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";


type FormValues = {
  oldPassword: string;
  newPassword: string;
  confirmNew: string;
  revokeOtherSessions: boolean;
};

const MIN_LEN = 12;

const ChangePassword: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    watch,
    reset,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmNew: "",
      revokeOtherSessions: false,
    },
    mode: "onChange",
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const oldPassword = watch("oldPassword");
  const newPassword = watch("newPassword");
  const confirmNew = watch("confirmNew");
  const revokeOtherSessions = watch("revokeOtherSessions");

  // Unsaved changes guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Keyboard shortcut: Ctrl/Cmd+S to submit
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const savePressed =
        (isMac && e.metaKey && key === "s") || (!isMac && e.ctrlKey && key === "s");

      if (!savePressed) return;

      e.preventDefault();
      if (isSubmitting) return;

      void trigger().then((ok) => {
        if (ok) {
          void handleSubmit(onSubmit)();
        }
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSubmit, trigger, isSubmitting]);

  // Password checks
  const checks = useMemo(() => {
    const length = newPassword.length >= MIN_LEN;
    const upper = /[A-Z]/.test(newPassword);
    const lower = /[a-z]/.test(newPassword);
    const digit = /[0-9]/.test(newPassword);
    const symbol = /[^A-Za-z0-9]/.test(newPassword);
    const differentFromOld = !!newPassword && newPassword !== oldPassword;
    const match = !!confirmNew && newPassword === confirmNew;

    const score =
      [length, upper, lower, digit, symbol, differentFromOld].filter(Boolean).length;
    return { length, upper, lower, digit, symbol, differentFromOld, match, score };
  }, [newPassword, confirmNew, oldPassword]);

  const strengthLabel = useMemo(() => {
    if (!newPassword) return "Empty";
    if (checks.score <= 2) return "Very Weak";
    if (checks.score === 3) return "Weak";
    if (checks.score === 4) return "Good";
    if (checks.score === 5) return "Strong";
    return "Very Strong";
  }, [newPassword, checks.score]);

  const strengthValue = (checks.score / 6) * 100;

  const generateStrongPassword = () => {
    // Small client-side generator using crypto for randomness
    const length = 16;
    const sets = [
      "ABCDEFGHJKLMNPQRSTUVWXYZ", // exclude ambiguous I/O
      "abcdefghijkmnopqrstuvwxyz", // exclude ambiguous l
      "0123456789",
      "!@#$%^&*()-_=+[]{};:,.<>/?",
    ];
    const all = sets.join("");
    const rand = (n: number) => {
      const buf = new Uint32Array(n);
      crypto.getRandomValues(buf);
      return Array.from(buf);
    };
    const pick = (pool: string, r: number) => pool[r % pool.length];

    // Ensure at least one from each set
    const base = sets.map((s, i) => pick(s, rand(1)[0] + i)).join("");
    const rest = rand(length - sets.length).map((r) => pick(all, r)).join("");
    const pwd = (base + rest)
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    // Fill both fields
    reset(
      {
        oldPassword,
        newPassword: pwd,
        confirmNew: pwd,
        revokeOtherSessions,
      },
      { keepDirty: true }
    );

    setSnackbar({
      open: true,
      message: "Generated a strong password.",
      severity: "info",
    });
  };

  const onSubmit = async (data: FormValues) => {
    // extra guard
    if (!checks.match) {
      setSnackbar({
        open: true,
        message: "New password and confirmation do not match.",
        severity: "error",
      });
      return;
    }
    if (checks.score < 4) {
      setSnackbar({
        open: true,
        message: "Please choose a stronger password (aim for Good+).",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      await axios.patch(
        `${baseUrl}/auth/password`,
        {
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
          // Server may ignore this flag; include if your API supports it
          revokeOtherSessions: data.revokeOtherSessions || false,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: "Password changed successfully!",
        severity: "success",
      });
      reset({ oldPassword: "", newPassword: "", confirmNew: "", revokeOtherSessions: false });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to change the password.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
       sx={{
          width: "100%",
          pt: "100px",
          px: "32px",
          backgroundColor: theme.palette.background.default,
        }}
    >
      {/* Top actions row */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mb: 2,
        }}
      >
        <Tooltip title="Generate strong password">
          <Button variant="outlined" onClick={generateStrongPassword} startIcon={<LockIcon />}>
            Generate
          </Button>
        </Tooltip>
        <Button variant="text" color="inherit" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Tooltip title="Save (Ctrl/Cmd + S)">
          <span>
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              disableElevation
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? "Saving…" : "Change Password"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {(loading || isSubmitting) && <LinearProgress sx={{ mb: 2 }} />}

      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, maxWidth: 640, mx: "auto" }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a unique, strong password. Aim for <b>{MIN_LEN}+</b> characters with a mix of
              letters, numbers, and symbols.
            </Typography>
          </Box>

          <Divider />

          {/* Old Password */}
          <Controller
            name="oldPassword"
            control={control}
            rules={{ required: "Old password is required." }}
            render={({ field }) => (
              <TextField
                {...field}
                type={showOld ? "text" : "password"}
                label="Current password"
                fullWidth
                autoFocus
                error={!!errors.oldPassword}
                helperText={errors.oldPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowOld((v) => !v)} edge="end" size="small">
                        {showOld ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* New Password */}
          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: "New password is required.",
              validate: {
                length: (v) =>
                  (v || "").length >= MIN_LEN || `Must be at least ${MIN_LEN} characters.`,
                upper: (v) => /[A-Z]/.test(v || "") || "Include an uppercase letter.",
                lower: (v) => /[a-z]/.test(v || "") || "Include a lowercase letter.",
                digit: (v) => /[0-9]/.test(v || "") || "Include a number.",
                symbol: (v) => /[^A-Za-z0-9]/.test(v || "") || "Include a symbol.",
                different: (v) => v !== oldPassword || "Must be different from current password.",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                type={showNew ? "text" : "password"}
                label="New password"
                fullWidth
                error={!!errors.newPassword}
                helperText={
                  errors.newPassword
                    ? Object.values(errors.newPassword.types ?? {}).shift()
                    : undefined
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew((v) => !v)} edge="end" size="small">
                        {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Strength meter */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Strength:
              </Typography>
              <Chip size="small" label={strengthLabel} />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={strengthValue}
              sx={{
                height: 8,
                borderRadius: 1,
              }}
            />
          </Box>

          {/* Requirements checklist */}
          <Stack spacing={1}>
            {[
              { ok: checks.length, label: `At least ${MIN_LEN} characters` },
              { ok: checks.upper, label: "Contains an uppercase letter (A-Z)" },
              { ok: checks.lower, label: "Contains a lowercase letter (a-z)" },
              { ok: checks.digit, label: "Contains a number (0-9)" },
              { ok: checks.symbol, label: "Contains a symbol (!@#$…)" },
              { ok: checks.differentFromOld, label: "Different from current password" },
            ].map((c, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                {c.ok ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CancelIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2">{c.label}</Typography>
              </Stack>
            ))}
          </Stack>

          {/* Confirm New Password */}
          <Controller
            name="confirmNew"
            control={control}
            rules={{
              required: "Please confirm your new password.",
              validate: (v) => v === newPassword || "Passwords do not match.",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                type={showConfirm ? "text" : "password"}
                label="Confirm new password"
                fullWidth
                error={!!errors.confirmNew}
                helperText={errors.confirmNew?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small">
                        {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {/* Extra security */}
          <FormControlLabel
            control={
              <Checkbox
                checked={revokeOtherSessions}
              
              />
            }
            label="Sign out my other sessions after changing password"
            onChange={(_, checked) =>
              reset(
                { oldPassword, newPassword, confirmNew, revokeOtherSessions: !!checked },
                { keepDirty: true }
              )
            }
          />

          <Divider />

          {/* Bottom actions mirror top (optional, can remove) */}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" color="inherit" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Tooltip title="Save (Ctrl/Cmd + S)">
              <span>
                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  disableElevation
                  disabled={loading || isSubmitting}
                >
                  {loading || isSubmitting ? "Saving…" : "Change Password"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangePassword;
